/**
 * Google Drive Storage Utility
 * Handles file operations with Google Drive API
 */

import { supabase } from './supabase'

class GoogleDriveStorage {
  constructor() {
    this.accessToken = null
    this.driveAPI = 'https://www.googleapis.com/drive/v3'
    this.uploadAPI = 'https://www.googleapis.com/upload/drive/v3'
  }

  /**
   * Initialize Google Drive access token from Supabase session
   */
  async initialize() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      console.log('Checking for Google Drive access token...')
      
      // First try provider_token (preferred)
      if (session?.provider_token) {
        this.accessToken = session.provider_token
        console.log('✅ Provider token found:', session.provider_token.substring(0, 20) + '...')
        return true
      }
      
      // Fallback: Try to get a new token using the refresh token
      if (session?.provider_refresh_token) {
        console.log('🔄 Trying to refresh Google token...')
        try {
          const refreshResponse = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              client_id: '59516443356-clql1qbv19k003lnm5uhe2rv4fbc1dqr.apps.googleusercontent.com',
              refresh_token: session.provider_refresh_token,
              grant_type: 'refresh_token'
            })
          })
          
          if (refreshResponse.ok) {
            const tokenData = await refreshResponse.json()
            this.accessToken = tokenData.access_token
            console.log('✅ Refreshed token obtained')
            return true
          }
        } catch (refreshError) {
          console.log('❌ Token refresh failed:', refreshError)
        }
      }
      
      console.log('❌ No Google Drive access token available')
      console.log('Available session keys:', Object.keys(session || {}))
      return false
    } catch (error) {
      console.error('Failed to initialize Google Drive:', error)
      return false
    }
  }

  /**
   * Get or create the app folder in Google Drive
   */
  async getAppFolder() {
    try {
      // Search for existing React Stash folder
      const searchResponse = await fetch(
        `${this.driveAPI}/files?q=name='React Stash' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
          },
        }
      )

      const searchData = await searchResponse.json()
      
      if (searchData.files && searchData.files.length > 0) {
        // If multiple folders exist, use the first one and optionally clean up duplicates
        if (searchData.files.length > 1) {
          console.log(`Found ${searchData.files.length} React Stash folders, using the first one`)
          // TODO: Could add cleanup logic here to remove duplicates
        }
        return searchData.files[0].id
      }

      // Create the folder if it doesn't exist
      console.log('Creating new React Stash folder...')
      const createResponse = await fetch(`${this.driveAPI}/files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: 'React Stash',
          mimeType: 'application/vnd.google-apps.folder',
        }),
      })

      if (!createResponse.ok) {
        throw new Error(`Failed to create folder: ${createResponse.statusText}`)
      }

      const createData = await createResponse.json()
      console.log('Created React Stash folder:', createData.id)
      return createData.id
    } catch (error) {
      console.error('Failed to get/create app folder:', error)
      throw error
    }
  }

  /**
   * Get or create user-specific folder
   */
  async getUserFolder(userId) {
    try {
      const appFolderId = await this.getAppFolder()
      
      // Search for user folder within app folder
      const searchResponse = await fetch(
        `${this.driveAPI}/files?q=name='${userId}' and '${appFolderId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
          },
        }
      )

      const searchData = await searchResponse.json()
      
      if (searchData.files && searchData.files.length > 0) {
        return searchData.files[0].id
      }

      // Create user folder
      const createResponse = await fetch(`${this.driveAPI}/files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: userId,
          mimeType: 'application/vnd.google-apps.folder',
          parents: [appFolderId],
        }),
      })

      const createData = await createResponse.json()
      return createData.id
    } catch (error) {
      console.error('Failed to get/create user folder:', error)
      throw error
    }
  }

  /**
   * Upload a file to Google Drive
   */
  async uploadFile(file, fileName, userId) {
    try {
      await this.initialize()
      const userFolderId = await this.getUserFolder(userId)

      // Create file metadata
      const metadata = {
        name: fileName,
        parents: [userFolderId],
      }

      // Create form data for multipart upload
      const form = new FormData()
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }))
      form.append('file', file)

      const response = await fetch(`${this.uploadAPI}/files?uploadType=multipart`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
        },
        body: form,
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Upload error response:', errorText)
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`)
      }

      const data = await response.json()
      return {
        id: data.id,
        name: data.name,
        size: data.size,
        mimeType: data.mimeType,
        createdTime: data.createdTime,
      }
    } catch (error) {
      console.error('Failed to upload file:', error)
      throw error
    }
  }

  /**
   * List files in user's folder
   */
  async listFiles(userId) {
    try {
      await this.initialize()
      const userFolderId = await this.getUserFolder(userId)

      const response = await fetch(
        `${this.driveAPI}/files?q='${userFolderId}' in parents and trashed=false&fields=files(id,name,size,mimeType,createdTime,modifiedTime)`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to list files: ${response.statusText}`)
      }

      const data = await response.json()
      return data.files || []
    } catch (error) {
      console.error('Failed to list files:', error)
      throw error
    }
  }

  /**
   * Download a file from Google Drive
   */
  async downloadFile(fileId) {
    try {
      await this.initialize()

      const response = await fetch(`${this.driveAPI}/files/${fileId}?alt=media`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`)
      }

      return await response.arrayBuffer()
    } catch (error) {
      console.error('Failed to download file:', error)
      throw error
    }
  }

  /**
   * Delete a file from Google Drive
   */
  async deleteFile(fileId) {
    try {
      await this.initialize()

      const response = await fetch(`${this.driveAPI}/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`)
      }

      return true
    } catch (error) {
      console.error('Failed to delete file:', error)
      throw error
    }
  }

  /**
   * Check if Google Drive is properly initialized
   */
  isInitialized() {
    return !!this.accessToken
  }
}

export const googleDrive = new GoogleDriveStorage()
